import type { Language } from '@/shared/i18n';

export type BlogPostKind = 'blog' | 'news';

export type BlogPostSection = {
  heading: string;
  paragraphs: readonly string[];
};

export type BlogPostImage = {
  alt: string;
  caption?: string;
  id: string;
  src: string;
};

export type LocalizedBlogPostContent = {
  bodyText: string;
  category: string;
  excerpt: string;
  imageAlt: string;
  readTime: string;
  seoDescription: string;
  seoKeywords: string;
  seoTitle: string;
  title: string;
};

export type BlogPost = {
  authorName: string;
  authorRole?: string;
  articleImages?: readonly BlogPostImage[];
  canonicalUrl?: string;
  content: Record<Language, LocalizedBlogPostContent>;
  imageCaption?: string;
  imageSrc: string;
  industry: Record<Language, string>;
  industryKey: string;
  kind: BlogPostKind;
  publishedAt: string;
  service: Record<Language, string>;
  serviceKey: string;
  slug: string;
  updatedAt?: string;
};

export type LocalizedBlogPost = Omit<
  BlogPost,
  'content' | 'industry' | 'service'
> &
  LocalizedBlogPostContent & {
    canonicalPath: string;
    industry: string;
    sections: readonly BlogPostSection[];
    service: string;
  };

const containsHtmlTag = (value: string) => /<\/?[a-z][\s\S]*>/i.test(value);

const normalizeParagraph = (paragraph: string) => {
  if (containsHtmlTag(paragraph)) {
    return paragraph.trim();
  }

  return paragraph
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .join(' ');
};

export const parseBlogTextToSections = (
  bodyText: string
): readonly BlogPostSection[] => {
  const blocks = bodyText
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map(block => block.trim())
    .filter(Boolean);

  if (blocks.length === 0) {
    return [];
  }

  const sections: BlogPostSection[] = [];
  let activeSection: BlogPostSection = {
    heading: '',
    paragraphs: []
  };

  const pushActiveSection = () => {
    if (activeSection.paragraphs.length === 0) {
      return;
    }

    sections.push(activeSection);
  };

  blocks.forEach(block => {
    const headingMatch = block.match(/^#{2,3}\s+(.+)$/);

    if (headingMatch) {
      pushActiveSection();
      activeSection = {
        heading: headingMatch[1].trim(),
        paragraphs: []
      };
      return;
    }

    if (/^#\s+/.test(block)) {
      return;
    }

    activeSection = {
      ...activeSection,
      paragraphs: [...activeSection.paragraphs, normalizeParagraph(block)]
    };
  });

  pushActiveSection();

  return sections;
};

export const getBlogPostPath = (post: Pick<BlogPost, 'kind' | 'slug'>) =>
  `/${post.kind}/${post.slug}`;

export const localizeBlogPost = (
  post: BlogPost,
  language: Language
): LocalizedBlogPost => {
  const localizedContent = post.content[language];

  return {
    ...post,
    ...localizedContent,
    canonicalPath: getBlogPostPath(post),
    industry: post.industry[language],
    sections: parseBlogTextToSections(localizedContent.bodyText),
    service: post.service[language]
  };
};
